"use client";

import { CountryInfo } from "@/store/types/countryTypes";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import React, { useEffect, useState } from "react";

interface BannerCarouselProps {
  slides?: CountryInfo["slides"];
  galleryImages?: string[];
}

const BannerCarousel: React.FC<BannerCarouselProps> = ({
  slides,
  galleryImages,
}) => {
  // Combine available images from galleryImages or slides
  const images = React.useMemo(() => {
    if (galleryImages && galleryImages.length > 0) {
      return galleryImages;
    }
    if (slides && slides.length > 0) {
      const slideImgs = slides.map((s) => s.image).filter(Boolean) as string[];
      if (slideImgs.length > 0) return slideImgs;
    }
    return [];
  }, [galleryImages, slides]);

  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-slide every 4 seconds
  useEffect(() => {
    if (images.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [images.length]);

  const getCardIndex = (offset: number) => {
    return (currentIndex + offset + images.length) % images.length;
  };

  const handleCardClick = (offset: number) => {
    if (offset !== 0) {
      setCurrentIndex((prev) => (prev + offset + images.length) % images.length);
    }
  };

  if (images.length === 0) return null;

  return (
    <div className="relative w-full max-w-[550px] lg:max-w-[580px] h-[260px] sm:h-[340px] lg:h-[380px] flex items-center justify-center select-none">
      <div className="relative w-full h-full flex items-center justify-center perspective-[1000px]">
        {[-1, 0, 1].map((offset) => {
          const idx = getCardIndex(offset);
          const isCenter = offset === 0;
          const isLeft = offset === -1;
          const imgUrl = images[idx];

          return (
            <motion.div
              key={imgUrl}
              onClick={() => handleCardClick(offset)}
              layout
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{
                scale: isCenter ? 1 : 0.84,
                x: isCenter ? "0%" : isLeft ? "-35%" : "35%",
                zIndex: isCenter ? 30 : 10,
                opacity: isCenter ? 1 : 0.5,
                rotateY: isCenter ? 0 : isLeft ? 10 : -10,
              }}
              transition={{
                layout: { duration: 0.85, ease: [0.25, 1, 0.5, 1] },
                scale: { duration: 0.85, ease: [0.25, 1, 0.5, 1] },
                x: { duration: 0.85, ease: [0.25, 1, 0.5, 1] },
                opacity: { duration: 0.6 },
                rotateY: { duration: 0.85 },
              }}
              className={`absolute w-[78%] sm:w-[75%] h-[82%] sm:h-[90%] rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border cursor-pointer ${isCenter
                ? "border-white/20 shadow-blue-950/60"
                : "border-white/10 hover:opacity-75"
                }`}
            >
              <Image
                src={imgUrl}
                alt={`Campus photo ${idx + 1}`}
                fill
                priority={isCenter}
                className="object-cover transition-transform duration-700 hover:scale-105"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
              <div
                className={`absolute inset-0 transition-opacity duration-700 ${isCenter
                  ? "bg-gradient-to-t from-black/40 via-transparent to-transparent"
                  : "bg-black/30"
                  }`}
              />
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default BannerCarousel;
